<template>
  <div class="page-opportunities">
    <BasePullToRefresh v-if="isTouchScreen" :pending="pending" @refresh="onRefreshRequest(currentPage)" />
    <div class="page-opportunities__header">
      <h1 class="page-opportunities__header-title">{{ $t('page.opportunities.header.title') }}</h1>
      <BaseButton class="page-opportunities__new" @click.native="openAddOpportunity" has-icon-left>
        <BaseIcon name="plus" />
        <span class="page-opportunities__new-label">{{ $t('page.opportunities.header.new-opportunity') }}</span>
      </BaseButton>
    </div>

    <div class="page-opportunities__data-table">
      <div class="page-opportunities__filters-panel">
        <PhoneFilterBar v-if="isPhone" :active-count="activeFilterCount" @clear-all="onClearAllFilters">
          <template #search>
            <BaseSearchBarNew class="page-opportunities__search" v-bind="searchBar" @input="onSearch" />
          </template>
          <template #filters>
            <OpportunitiesFilters :searchFilters="filters" v-on="filterListeners" />
          </template>
        </PhoneFilterBar>
        <template v-else>
          <OpportunitiesFilters :searchFilters="filters" v-on="filterListeners" />
          <BaseSearchBarNew class="page-opportunities__search" v-bind="searchBar" @input="onSearch" />
        </template>
      </div>
      <div class="page-opportunities__results-section" ref="topAnchor">
        <span class="page-opportunities__count">
          <span class="number-monospace">{{ formatLocalizedNumber(totalCount) }}</span>
          {{ ' ' + $t('page.opportunities.results') }}
        </span>
        <BasePhoneListControls
          v-if="isPhone && hasResults"
          class="page-opportunities__phone-controls"
          :selecting.sync="phoneSelecting"
          @sort="phoneSortOpen = true"
        />
        <BaseRefreshButton :pending="pending" @refresh="onRefreshRequest(currentPage)" />
      </div>
      <div v-if="hasResults" class="page-opportunities__content">
        <OpportunitiesPhoneList
          v-if="isPhone"
          :opportunities="opportunitiesDetailed"
          :statuses="opportunityStatuses"
          :labels="opportunityLabels"
          :users="users || []"
          :assignable-users="assignableUsers"
          :updating-status-ids="updatingStatusIds"
          :show-votes="tenantHasGonogoAccess()"
          :show-documents="tenantHasDocumentsAccess()"
          :selecting.sync="phoneSelecting"
          :sort-open.sync="phoneSortOpen"
          :sorting-field="sortingField"
          :sorting-order="sortingOrder"
          :has-more="page < totalPages"
          :loading-more="loadingMore"
          :load-more-error="loadMoreError"
          :pending="pending"
          @open="goToOpportunity"
          @load-more="loadMore"
          @sort="onPhoneSort"
          @status-change="onStatusChange"
          @labels-change="onLabelsChange"
          @assign="onAssign"
          @synchronize="onSynchronizeOpportunityClick"
          @vote="openVoteDialog"
          @go-nogo="(opportunity) => openGoNogo(opportunity.id)"
          @view-tender="(opportunity) => openTenderDetails(opportunity.tender.id)"
          @refresh="onRefreshRequest()"
        />
        <TableNormal
          v-else
          v-model="selectedOpportunities"
          :fixed-header="true"
          :headers="_headers"
          :items="opportunitiesDetailed"
          :items-per-page="itemsPerPage"
          :page.sync="currentPage"
          hide-default-footer
          item-key="id"
          @click:row="goToOpportunity"
          @update:sort-by="setSortingField"
          @update:sort-desc="setSortingOrder"
          :mobile-breakpoint="1200"
          show-select
        >
          <template v-slot:header.data-table-select="{}">
            <FormCheckbox
              :indeterminate="partiallySelected"
              :afterIndeterminate="false"
              :onChange="onSelectAllClick"
              :value="allAreSelected"
              class="tenders-data-table__check-all"
            />
            <OpportunitiesActionManager
              v-if="hasSelection"
              :opportunities="selectedOpportunities"
              @refresh-opportunities="onRefreshOpportunities"
              @download-documents-started="unselectAll"
            />
          </template>
          <template v-slot:item.data-table-select="{ isSelected, select }">
            <FormCheckbox :value="isSelected" @input="select($event)" @click.native.stop />
          </template>
          <template v-slot:item.name="{ item }">
            <div class="page-opportunities__opp-title" :id="'item-' + item.id">
              <OpportunityStatusType v-if="item.status" :opportunity-status="item.status" condensed />
              <div class="page-opportunities__opp-title--content">
                <TextHighlight :content="item.name" isPlain class="page-opportunities__opp-title--name"></TextHighlight>

                <div class="page-opportunities__opp-title--meta">
                  <BaseLinkedTender
                    v-if="item.tender"
                    :tenderId="item.tender.id"
                    :source="item.tender.source.id"
                    :title="item.tender.title"
                    :referenceNumber="item.tender.source.referenceNumber"
                    onlyShowRefNumber
                    isSmall
                  />
                  <template v-else>
                    <BaseChip size="sm" preset="soft" tone="neutral" :interactive="false">
                      {{ getOpportunitySourceName(item.source) }}
                      <span v-if="item.tenderId"> · {{ $t('page.opportunities.new.tender-unavailable') }}</span>
                    </BaseChip>
                  </template>
                  <BaseChip
                    v-if="tenantHasDocumentsAccess() && item.documents.length > 0"
                    size="sm"
                    preset="soft"
                    tone="neutral"
                    icon="file"
                    v-tooltip="{
                      content: $t('page.opportunities.view-documents'),
                      placement: 'top',
                    }"
                    @click.stop="goToOpportunity(item)"
                  >
                    {{ item.documents.length }}
                  </BaseChip>
                </div>
              </div>
            </div>
          </template>
          <template v-slot:item.oppClosingDate="{ item }">
            <div class="page-opportunities__opp-closing-date">
              <template v-if="item.tender && item.tender.tenderPeriod && item.tender.tenderPeriod.endDate">
                {{ formatUserShortDate(item.tender.tenderPeriod.endDate) }}
                <BaseTag
                  v-if="item.tender.daysBeforeClosing && item.tender.daysBeforeClosing !== 0"
                  icon="clock"
                  preset="soft"
                  :tone="getClosingCountdownTone(item.tender.daysBeforeClosing)"
                >
                  {{ getDhm(item.tender.daysBeforeClosing).split(' ')[0] }}
                </BaseTag>
              </template>
              <template v-else>
                {{ formatUserShortDate(item.endDate) }}
                <BaseTag
                  v-if="item.daysBeforeClosing && item.daysBeforeClosing !== 0"
                  icon="clock"
                  preset="soft"
                  :tone="getClosingCountdownTone(item.daysBeforeClosing)"
                >
                  {{ getDhm(item.daysBeforeClosing).split(' ')[0] }}
                </BaseTag>
              </template>
            </div>
          </template>
          <template v-slot:item.createdAt="{ item }">
            <div class="page-opportunities__creator">
              <template v-if="getUserFullName(item.authorId)">
                <div class="page-opportunities__created-at">
                  {{ formatUserDate(item.createdAt) }}
                </div>
                <div>{{ getUserFullName(item.authorId) }}</div>
              </template>
              <template v-else>{{ $t('unknown.user') }}</template>
            </div>
          </template>
          <template v-slot:item.tenderStatus="{ item }">
            <div v-if="item.tender && item.tender.status" class="tenders-data-table__status">
              <TenderStatusTag :status="item.tender.status" />
            </div>
            <div v-else class="page-opportunities__external-source">-</div>
          </template>
          <template v-slot:item.status="{ item }">
            <div @click.stop="" class="page-opportunities__status" :class="`status-${item.id}`">
              <BaseStatusMenu
                :items="opportunityStatuses"
                :ref="`statusMenu-${item.id}`"
                :current-status="item.status"
                :attach="`.status-${item.id}`"
                @change="(statusId) => onStatusChange(item, statusId)"
                searchAllowed
              />
            </div>
          </template>
          <template v-slot:item.labels="{ item }">
            <div @click.stop="" class="page-opportunities__labels" :class="`labels-${item.id}`">
              <BaseLabelsMenu
                :items="opportunityLabels"
                :value="item.labels || []"
                :attach="`.labels-${item.id}`"
                @change="(labelIds) => onLabelsChange(item, labelIds)"
              />
            </div>
          </template>
          <template v-slot:item.votes="{ item }">
            <div>
              <div
                v-if="item.finalDecision"
                class="page-opportunities__final-decision"
                @click.stop="openGoNogo(item.id)"
              >
                <v-popover
                  placement="top"
                  trigger="hover"
                  popoverClass="page-opportunities__vote-tooltip"
                  :delay="{ show: 600, hide: 0 }"
                >
                  <BaseVoteTicket
                    :name="finalDecisionVoteLabel(item.finalDecision)"
                    :vote="finalDecisionDisplayVote(item.finalDecision)"
                    is-final
                  />
                  <template v-slot:popover>
                    <OpportunityVoteTooltipDetails
                      :name="getUserFullName(item.finalDecision.decidedBy)"
                      :vote-type="$t('go-nogo.final-decision.header-tag-tooltip')"
                      :reasons="finalDecisionDetails(item.finalDecision)"
                    />
                  </template>
                </v-popover>
              </div>
              <div
                v-else-if="item.votes.length < 1"
                class="page-opportunities__vote-action"
                role="button"
                tabindex="0"
                @click.stop="openVoteDialog(item)"
                @keydown.enter.prevent.stop="openVoteDialog(item)"
                @keydown.space.prevent.stop="openVoteDialog(item)"
              >
                <BaseIcon name="vote" />
                {{ $t('page.opportunities.vote') }}
              </div>
              <div v-else class="page-opportunities__vote-section">
                <div v-if="false">{{ getConfirmedVotes(item.votes).length + '/' + item.votes.length }}</div>
                <div class="page-opportunities__votes" @click.stop="openGoNogo(item.id)">
                  <v-popover
                    v-for="(vote, index) in sortedVotes(item.votes).slice(0, 3)"
                    :key="index"
                    placement="top"
                    trigger="hover"
                    popoverClass="page-opportunities__vote-tooltip"
                    :delay="{ show: 600, hide: 0 }"
                  >
                    <BaseVoteTicket :name="getUserFullName(vote.userId)" :vote="vote.vote" />
                    <template v-slot:popover>
                      <OpportunityVoteTooltipDetails
                        :name="getUserFullName(vote.userId)"
                        :vote-type="getVoteTypeLabel(vote)"
                        :reasons="getVoteReasonsLabel(vote)"
                      />
                    </template>
                  </v-popover>
                  <v-popover
                    placement="top"
                    trigger="hover"
                    popoverClass="page-opportunities__vote-tooltip"
                    v-if="item.votes.length > 3"
                  >
                    <div class="base-assignation__more-people">
                      +{{ item.votes.slice(-(item.votes.length - 3)).length }}
                    </div>
                    <template v-slot:popover>
                      <div class="page-opportunities__vote-tooltip-content">
                        <div
                          v-for="(vote2, index) in sortedVotes(item.votes).slice(-(item.votes.length - 3))"
                          :key="index"
                          class="page-opportunities__vote-tooltip-item"
                        >
                          <OpportunityVoteTooltipDetails
                            :name="getUserFullName(vote2.userId)"
                            :vote-type="getVoteTypeLabel(vote2)"
                            :reasons="getVoteReasonsLabel(vote2)"
                          />
                          <BaseVoteTicket :name="getUserFullName(vote2.userId)" :vote="vote2.vote" />
                        </div>
                      </div>
                    </template>
                  </v-popover>
                </div>
              </div>
            </div>
          </template>
          <template v-slot:item.assignation="{ item }">
            <div @click.stop="" :class="`assignee-${item.id}`">
              <BaseAssignation
                :value="item.assignees"
                @change="onAssign(item, ...arguments)"
                :attach="`.assignee-${item.id}`"
              />
            </div>
          </template>
          <template v-slot:item.sync="{ item }">
            <div class="page-opportunities__sync">
              <div v-for="(oppSync, index) in item.syncs" :key="index">
                <BaseEntitySync :entitySync="oppSync" @synchronize="onSynchronizeOpportunityClick(oppSync, item)" />
              </div>
            </div>
          </template>
        </TableNormal>
        <div v-if="totalPages > 1 && !isPhone" class="tenders-data-table__pagination">
          <TablePagination
            v-model="currentPage"
            :length="totalPages"
            :total-visible="isMobile ? 6 : 10"
            @input="onChangePage"
          />
        </div>
      </div>
      <TableState
        v-if="!hasResults"
        :state-loading="!hasResults && pending"
        :state-no-data="!hasResults && !pending"
        :state-text="tableStateText"
        :state-sub-text="!pending && hasNoOpportunity ? $t('page.opportunities.no-opportunity-subtitle') : ''"
      >
        <template #action v-if="!pending && hasNoOpportunity">
          <BaseButton @click.native="openAddOpportunity" has-icon-left>
            <BaseIcon name="plus" />
            {{ $t('page.opportunities.header.new-opportunity') }}
          </BaseButton>
        </template>
      </TableState>
    </div>
    <v-overlay :value="pending" absolute opacity="0.5" color="#ffffff" z-index="1"></v-overlay>
    <GoNoGoDialog
      ref="goNoGoDialog"
      :userVote="currentUserVote"
      :changeVote="currentUserVote && currentUserVote.isPending ? false : !!currentUserVote"
      :opportunityId="currentOpportunityId"
    />
    <AddOpportunityDialog ref="addOpportunityDialog" />
    <SyncOpportunityDialog ref="syncOpportunityDialog" @confirm="synchronizeOpportunity" />
  </div>
</template>

<script>
import AddOpportunityDialog from '@/components/page-opportunities/AddOpportunityDialog.vue';
import BaseAssignation from '@/components/base/BaseAssignation';
import BaseLabelsMenu from '@/components/base/BaseLabelsMenu.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import BaseChip from '@/components/base/BaseChip.vue';
import BaseEntitySync from '@/components/base/BaseEntitySync';
import BaseIcon from '@/components/base/BaseIcon';
import BaseLinkedTender from '@/components/base/BaseLinkedTender';
import BasePhoneListControls from '@/components/base/BasePhoneListControls.vue';
import BasePullToRefresh from '@/components/base/BasePullToRefresh.vue';
import BaseRefreshButton from '@/components/base/BaseRefreshButton.vue';
import BaseTag from '@/components/base/BaseTag.vue';
import BaseVoteTicket from '@/components/base/BaseVoteTicket';
import GoNoGoDialog from '@/components/page-go-nogo/GONoGoDialog.vue';
import TableNormal from '@/components/table/TableNormal.vue';
import TablePagination from '@/components/table/TablePagination.vue';
import TableState from '@/components/table/TableState.vue';
import TextHighlight from '@/components/text-highlight/TextHighlight';
import TenderStatusTag from '@/components/page-tender-details/TenderStatusTag.vue';
import SyncOpportunityDialog from '@/components/page-opportunity-details/SyncOpportunityDialog';
import BaseStatusMenu from '@/components/base/BaseStatusMenu.vue';
import OpportunityStatusType from '@/components/page-admin-opportunities/OpportunityStatusType.vue';
import OpportunitiesFilters from '@/components/page-opportunities/OpportunitiesFilters.vue';
import PhoneFilterBar from '@/components/filters/PhoneFilterBar.vue';
import BaseSearchBarNew from '@/components/base/BaseSearchBarNew.vue';
import FormCheckbox from '@/components/form/FormCheckbox.vue';

import axios from 'axios';
import { bus } from '@/main';
import { formatUserShortDate, formatUserDate, getDhm } from '@/util/date-util';
import { getOppSortingField, getSortingOrder } from '@/util/sorting-util';
import { mapActions, mapGetters } from 'vuex';
import PlatformName from '@/mixins/platform-name-mixin';
import DateMixin from '@/mixins/date-mixin';
import IsMobileMixin from '@/mixins/is-mobile-mixin';
import DebounceMixin from '@/mixins/debounce-mixin';
import { tenantHasDocumentsAccess, tenantHasGonogoAccess } from '@/util/module-access-util';
import { str2bool } from '@/util/string-util';
import { getRouteQueryAsArray, getRouteQueryAsNumber } from '@/util/path-util';
import { formatLocalizedNumber } from '@/util/number-util';
import { getClosingCountdownTone } from '@/util/tender-closing-countdown';
import { normalizeVoteReasonNewlines } from '@/util/go-nogo-vote-reasons';
import { toFinalDecisionDisplayVote } from '@/util/go-nogo-final-decision';
import OpportunitiesActionManager from '@/components/page-opportunities/OpportunitiesActionManager.vue';
import OpportunitiesPhoneList from '@/components/page-opportunities/OpportunitiesPhoneList.vue';
import OpportunityVoteTooltipDetails from '@/components/page-opportunities/OpportunityVoteTooltipDetails.vue';
import { highlightPhoneRow } from '@/util/highlight-phone-row';
import { assignableUsers } from '@/util/user-util';
import { countSetFilters } from '@/util/filter-count';
import { viewport } from '@/util/viewport';

export default {
  name: 'PageOpportunities',
  mixins: [PlatformName, IsMobileMixin, DateMixin, DebounceMixin],
  components: {
    OpportunityStatusType,
    BaseLabelsMenu,
    BaseStatusMenu,
    OpportunitiesFilters,
    AddOpportunityDialog,
    BaseAssignation,
    BaseButton,
    BaseChip,
    BaseEntitySync,
    BaseIcon,
    BaseLinkedTender,
    BasePhoneListControls,
    BasePullToRefresh,
    BaseRefreshButton,
    BaseSearchBarNew,
    BaseTag,
    BaseVoteTicket,
    GoNoGoDialog,
    TableNormal,
    TablePagination,
    TableState,
    TextHighlight,
    TenderStatusTag,
    SyncOpportunityDialog,
    OpportunitiesActionManager,
    OpportunitiesPhoneList,
    OpportunityVoteTooltipDetails,
    FormCheckbox,
    PhoneFilterBar,
  },
  computed: {
    ...mapGetters('auth', ['currentUser']),
    ...mapGetters('tenant', ['users', 'tenant', 'roleUserOnly']),
    ...mapGetters('opportunities', [
      'opportunities',
      'totalCount',
      'totalPages',
      'page',
      'pending',
      'filters',
      'continuous',
      'loadingMore',
      'loadMoreError',
      'sortingField',
      'sortingOrder',
    ]),
    ...mapGetters('opportunitySyncProfiles', ['opportunitySyncProfiles']),
    ...mapGetters('opportunitySync', ['opportunitySyncs']),
    ...mapGetters('opportunityStatuses', ['opportunityStatuses']),
    ...mapGetters('opportunityLabels', ['opportunityLabels']),
    ...mapGetters('tenantModules', ['tenantModules']),
    ...mapGetters('goNoGo', ['opportunityVoteReasons']),
    isPhone() {
      return viewport.size === 'phone';
    },
    isTouchScreen() {
      return !viewport.canHover;
    },
    // The chips and the search, in the page's row or a phone's filter bar.
    filterListeners() {
      return {
        'status-filter-change': this.onStatusFilterChange,
        'voter-filter-change': this.onVoterFilterChange,
        'document-filter-change': this.onDocumentFilterChange,
        'creator-filter-change': this.onCreatorFilterChange,
        'assignee-filter-change': this.onAssigneeFilterChange,
        'label-filter-change': this.onLabelFilterChange,
      };
    },
    searchBar() {
      return {
        name: 'search-opportunities',
        value: this.filters.query,
        placeholder: this.$t('page.opportunities.search-bar-placeholder'),
        hasDebounce: true,
      };
    },
    activeFilterCount() {
      const { opportunityStatusId, voterId, hasDocuments, authorId, assigneeId, labelId } = this.filters;
      return countSetFilters([opportunityStatusId, voterId, hasDocuments, authorId, assigneeId, labelId]);
    },
    assignableUsers() {
      return assignableUsers(this.currentUser, this.users, this.roleUserOnly);
    },
    _headers() {
      return this.headers.filter((header) => header.show !== false);
    },
    headers() {
      return [
        {
          text: this.$t('page.opportunities.data-table-titles.title'),
          value: 'name',
          sortable: false,
        },
        {
          text: this.$t('page.opportunities.data-table-titles.emitter'),
          value: 'emitter',
          sortable: false,
          width: '12%',
        },
        {
          text: this.$t('page.opportunities.data-table-titles.author'),
          value: 'createdAt',
          sortable: true,
          width: '10%',
        },
        {
          text: this.$t('page.opportunities.data-table-titles.tender-status'),
          value: 'tenderStatus',
          sortable: false,
          width: '6%',
        },
        {
          text: this.$t('page.tenders.data-table-titles.closing'),
          value: 'oppClosingDate',
          sortable: true,
          width: '5%',
        },
        {
          text: this.$t('page.opportunities.data-table-titles.status'),
          value: 'status',
          sortable: true,
          width: '5%',
        },
        {
          text: this.$t('page.opportunities.data-table-titles.labels'),
          value: 'labels',
          sortable: false,
          width: '12%',
        },
        {
          text: this.$t('page.opportunities.data-table-titles.votes'),
          value: 'votes',
          sortable: false,
          show: this.tenantHasGonogoAccess(),
          width: '5%',
        },
        {
          text: this.$t('page.opportunities.data-table-titles.assignees'),
          value: 'assignation',
          sortable: false,
          width: '7%',
        },
        {
          text: this.$t('page.opportunities.data-table-titles.synchronization'),
          value: 'sync',
          sortable: false,
          show: this.opportunitySyncProfiles.length > 0,
          width: '7%',
        },
      ];
    },
    hasResults() {
      return this.opportunities.length > 0;
    },
    hasNoOpportunity() {
      return this.totalCount <= 0;
    },
    tableStateText() {
      if (this.pending) {
        return this.$t('page.opportunities.loading');
      } else {
        if (this.hasNoOpportunity) {
          return this.$t('page.opportunities.no-opportunity');
        }
        return this.$t('page.opportunities.no-result');
      }
    },
    activeSyncProfiles() {
      return this.opportunitySyncProfiles.filter((profile) => profile.active);
    },
    opportunitiesDetailed() {
      return this.opportunities.map((opportunity) => {
        opportunity.syncs = this.activeSyncProfiles.map((profile) => {
          const syncObj = this.opportunitySyncs.find(
            (oppSync) => oppSync.profileId === profile.id && oppSync.opportunityId === opportunity.id,
          );
          return { profile, sync: syncObj };
        });
        return opportunity;
      });
    },
    // Scoped to the rows on screen: the store also holds entries fetched by other views (details header,
    // quotes page), and polling for those would never end since only this page's ids get refreshed here.
    hasInProgressSyncs() {
      return this.opportunitiesDetailed.some((opportunity) =>
        opportunity.syncs.some((oppSync) => oppSync.sync && oppSync.sync.status === 'IN_PROGRESS'),
      );
    },
    allAreSelected() {
      return this.opportunities.length === this.selectedOpportunities.length;
    },
    hasSelection() {
      return this.selectedOpportunities.length > 0;
    },
    partiallySelected() {
      return this.hasSelection && this.opportunities.length !== this.selectedOpportunities.length;
    },
  },
  data() {
    return {
      lastViewedOpportunityId: null,
      selectedOpportunities: [],
      currentOpportunity: null,
      currentOpportunityId: null,
      currentUserVote: null,
      itemsPerPage: 25,
      currentPage: 1,
      syncInterval: null,
      phoneSelecting: false,
      phoneSortOpen: false,
      updatingStatusIds: [],
    };
  },
  methods: {
    ...mapActions('opportunities', [
      'searchOpportunities',
      'loadMoreOpportunities',
      'setContinuous',
      'setCustomSortingField',
      'setCustomSortingOrder',
      'setHasDocumentsSearchFilter',
      'setOpportunityStatusSearchFilter',
      'setVoterSearchFilter',
      'setCreatorSearchFilter',
      'setAssigneeSearchFilter',
      'setLabelSearchFilter',
      'setQuerySearchFilter',
      'setOpportunities',
    ]),
    ...mapActions('opportunity', ['getOpportunity', 'editOpportunity', 'assign', 'assignLabels']),
    ...mapActions('opportunitySyncProfiles', ['getOpportunitySyncProfiles']),
    ...mapActions('opportunitySync', ['getOpportunitySyncs', 'syncOpportunity']),
    ...mapActions('opportunityStatuses', ['getOpportunityStatuses']),
    ...mapActions('opportunityLabels', ['getOpportunityLabels']),
    ...mapActions('goNoGo', ['setUsers', 'setCurrentUser']),
    tenantHasGonogoAccess,
    tenantHasDocumentsAccess,
    formatUserShortDate,
    formatUserDate,
    formatLocalizedNumber,
    getDhm,
    getClosingCountdownTone,
    sortedVotes(votes) {
      const augmentedVotes = [];
      votes.forEach((vote) => {
        let name = this.getUserFullName(vote.userId);
        augmentedVotes.push({ ...vote, name });
      });
      return augmentedVotes.sort((a, b) => a.name.localeCompare(b.name));
    },
    getVoteTypeLabel(vote) {
      if (vote.isPending || !vote.vote) {
        return this.$t('go-nogo.pending');
      }
      const voteType = vote.vote;
      if (voteType === 'YES' || voteType === 'GO') {
        return this.$t('go-nogo.dialog.go');
      }
      if (voteType === 'NO' || voteType === 'NOGO') {
        return this.$t('go-nogo.dialog.no-go');
      }
      if (voteType === 'NEUTRAL' || voteType === 'INDIFFERENT') {
        return this.$t('go-nogo.dialog.indifferent');
      }
      return voteType;
    },
    getVoteReasonsLabel(vote) {
      if (vote.transformedReasons?.length) {
        return vote.transformedReasons
          .map((reason) => (typeof reason === 'string' ? reason : reason?.reason))
          .filter(Boolean)
          .join(', ');
      }
      if (vote.reasons?.length) {
        return vote.reasons.join(', ');
      }
      return null;
    },
    finalDecisionDisplayVote(finalDecision) {
      return toFinalDecisionDisplayVote(finalDecision.vote);
    },
    finalDecisionVoteLabel(finalDecision) {
      const displayVote = this.finalDecisionDisplayVote(finalDecision);
      return displayVote ? this.$t(`go-nogo.${displayVote.toLowerCase()}`) : '';
    },
    // Reasons and comment are stored separately on a final decision — join them for the tooltip.
    finalDecisionDetails(finalDecision) {
      const parts = [...(finalDecision.reasons || [])];
      if (finalDecision.comment) {
        parts.push(normalizeVoteReasonNewlines(finalDecision.comment));
      }
      return parts.length ? parts.join(', ') : null;
    },
    getUserFullName(authorId) {
      if (this.users) {
        const user = this.users.find((user) => user.id === authorId);
        return user ? user.fullName : this.$t('unknown.user');
      }
    },
    setSortingField(field) {
      const sortingField = getOppSortingField(field[0]);
      this.setCustomSortingField({ sortingField });
      this.onRefreshRequest();
    },
    setSortingOrder(order) {
      const sortingOrder = getSortingOrder(order[0]);
      this.setCustomSortingOrder({ sortingOrder });
      this.onRefreshRequest();
    },
    getConfirmedVotes(votes) {
      return votes.filter((vote) => !vote.isPending);
    },
    goToOpportunity(opportunity) {
      this.$router.push({
        name: 'opportunity-details',
        params: { opportunityId: opportunity.id },
      });
    },
    openGoNogo(opportunityId) {
      this.$router.push({
        name: 'opportunity-go-nogo',
        params: { opportunityId: opportunityId },
      });
    },
    openTenderDetails(tenderId) {
      if (!tenderId) return;
      this.$router.push({ name: 'tender-details', params: { id: tenderId } });
    },
    openVoteDialog(opportunity) {
      this.currentOpportunity = opportunity;
      this.currentOpportunityId = opportunity.id;
      this.setCurrentUser(this.currentUser);
      this.currentUserVote = opportunity.votes.find((vote) => vote.userId === this.currentUser.id) || null;
      this.$refs.goNoGoDialog.openDialog();
    },
    onSynchronizeOpportunityClick(oppSync, opportunity) {
      this.currentOpportunity = opportunity;
      // Show the dialog for new syncs or failed syncs (let the user correct field values before retrying)
      if (!oppSync.sync || oppSync.sync.status === 'FAILED') {
        this.$refs.syncOpportunityDialog.openDialog(opportunity, oppSync);
      } else {
        // Already synced: re-sync directly; the backend reuses the last submitted field values.
        this.synchronizeOpportunity(oppSync, {});
      }
    },
    synchronizeOpportunity(oppSync, fieldValues) {
      if (oppSync.sync && oppSync.sync.status && oppSync.sync.status === 'IN_PROGRESS') {
        this.$toast.warning(this.$t('opportunity-sync.statuses.in-progress', { date: oppSync.sync.lastSyncDate }));
        return;
      }
      this.syncOpportunity({
        profileId: oppSync.profile.id,
        opportunityId: this.currentOpportunity.id,
        fieldValues,
      }).then(() => {
        this.$toast.success(this.$t('opportunity-sync.success'));
        this.fetchOpportunitySyncs();
      });
    },
    openAddOpportunity() {
      this.$refs.addOpportunityDialog.openDialog();
    },
    onRefreshRequest(page = 1) {
      // The phone list grows from page 1.
      this.searchOpportunities(this.continuous ? 1 : page);
    },
    loadMore() {
      // Asking for a page now would cancel the search that's running.
      if (this.pending || this.page >= this.totalPages) return;
      this.loadMoreOpportunities(this.page + 1);
    },
    onPhoneSort({ field, desc }) {
      this.setCustomSortingField({ sortingField: getOppSortingField(field) });
      this.setCustomSortingOrder({ sortingOrder: getSortingOrder(desc) });
      this.onRefreshRequest();
    },
    onChangePage(page = 1) {
      // Cancel a pending filter-change search (it would reset to page 1) so paginating wins and the
      // list doesn't snap back after the 700ms debounce fires.
      this.debounce(() => {}, 0, true);
      this.onRefreshRequest(page);
      this.$refs.topAnchor.scrollIntoView({ block: 'end', behavior: 'smooth', inline: 'start' });
    },
    refreshOpportunitySyncs() {
      return this.getOpportunitySyncProfiles({}).then(() => this.fetchOpportunitySyncs());
    },
    fetchOpportunitySyncs() {
      const opportunityIds = (this.opportunities || []).map((o) => o.id);
      const profileIds = this.activeSyncProfiles.map((profile) => profile.id);
      if (!opportunityIds.length || !profileIds.length) return Promise.resolve();

      // Only the latest request matters: cancel the previous one so a slow response for another page (or an
      // older poll) can't land after a fresher one and show stale statuses.
      if (this._syncsCancelSource) this._syncsCancelSource.cancel('Newer opportunity syncs request');
      this._syncsCancelSource = axios.CancelToken.source();
      return this.getOpportunitySyncs({ profileIds, opportunityIds, cancelToken: this._syncsCancelSource });
    },
    handleInProgressSyncs() {
      clearInterval(this.syncInterval);
      // No polling while this kept-alive page is deactivated: the view on screen refreshes its own statuses,
      // and `activated` calls this again to resume.
      if (this.hasInProgressSyncs && this._isActive !== false) {
        this.syncInterval = setInterval(() => {
          this.fetchOpportunitySyncs();
        }, 10000);
      }
    },
    onStatusFilterChange(statusIds) {
      this.setOpportunityStatusSearchFilter(statusIds);
      this.debouncedSearch();
    },
    onDocumentFilterChange(documentFilter) {
      this.setHasDocumentsSearchFilter(documentFilter);
      this.debouncedSearch();
    },
    onVoterFilterChange(voterIds) {
      this.setVoterSearchFilter(voterIds);
      this.debouncedSearch();
    },
    onCreatorFilterChange(authorIds) {
      this.setCreatorSearchFilter(authorIds);
      this.debouncedSearch();
    },
    onAssigneeFilterChange(assigneeIds) {
      this.setAssigneeSearchFilter(assigneeIds);
      this.debouncedSearch();
    },
    onLabelFilterChange(labelIds) {
      this.setLabelSearchFilter(labelIds);
      this.debouncedSearch();
    },
    onClearAllFilters() {
      this.setOpportunityStatusSearchFilter([]);
      this.setVoterSearchFilter([]);
      this.setHasDocumentsSearchFilter(null);
      this.setCreatorSearchFilter(null);
      this.setAssigneeSearchFilter([]);
      this.setLabelSearchFilter([]);
      this.onRefreshRequest(1);
    },
    // Debounce so a multi-select session fires one search once the user pauses, not one per
    // click (700ms). Pagination/sorting stay immediate via onRefreshRequest.
    debouncedSearch() {
      this.debounce(() => this.onRefreshRequest(1), 700);
    },
    onAssign(opportunity, userIds) {
      this.assign({ opportunityId: opportunity.id, userIds });
    },
    onLabelsChange(opportunity, labelIds) {
      this.assignLabels({ opportunityId: opportunity.id, labelIds }).catch((error) => {
        this.$toast.error(error);
      });
    },
    onStatusChange(opportunity, newStatus) {
      // The table's menu shows the wait; the phone list has no menu, its row does.
      const setLoading = (loading) => {
        const menu = this.$refs[`statusMenu-${opportunity.id}`];
        if (menu) menu.setLoading(loading);
        this.updatingStatusIds = loading
          ? [...this.updatingStatusIds, opportunity.id]
          : this.updatingStatusIds.filter((id) => id !== opportunity.id);
      };
      setLoading(true);
      this.getOpportunity({ id: opportunity.id })
        .then((opp) => {
          const body = {
            name: opp.name,
            evaluationGridTemplateId: null,
            statusId: newStatus ? newStatus.id : null,
            startDate: opp.startDate,
            endDate: opp.endDate,
            source: opp.source,
            emitter: opp.emitter,
            notes: opp.notes,
            version: opp.version,
          };
          return this.editOpportunity({
            opportunityId: opportunity.id,
            opportunity: body,
          });
        })
        .finally(() => setLoading(false));
    },
    highlightOpportunity(opportunityId) {
      const elementId = `item-${opportunityId}`;
      this.$nextTick(() => this.goToElement(elementId));
    },
    goToElement(elementId) {
      const element = document.getElementById(elementId);

      if (element) {
        const tr = element.closest('tr');
        element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        if (!tr) {
          highlightPhoneRow(element);
          return;
        }
        tr.className = 'highlight';
        setTimeout(() => {
          tr.className = '';
        }, 1200);
      }
    },
    onSearch(query) {
      this.setQuerySearchFilter({ query });
      this.onRefreshRequest();
    },
    onSelectAllClick() {
      if (this.partiallySelected || this.allAreSelected) this.unselectAll();
      else this.selectAll();
    },
    selectAll() {
      this.selectedOpportunities = [...this.opportunities];
    },
    unselectAll() {
      this.selectedOpportunities = [];
    },
    onRefreshOpportunities() {
      this.unselectAll();
      this.onRefreshRequest();
    },
  },
  watch: {
    // The tenant roster lands after this page is created, so handing it over once in `created`
    // passed `undefined`. Votes resolve their author against it, so wait for it instead.
    users: {
      immediate: true,
      handler(users) {
        if (users) this.setUsers(users);
      },
    },
    page() {
      this.currentPage = this.page;
    },
    isPhone: {
      immediate: true,
      handler(isPhone, wasPhone) {
        this.setContinuous(isPhone);
        if (wasPhone === undefined || this._isActive === false) return;
        // Crossing the phone width (a rotation, a resized window): the phone list grows from
        // page 1 and the table shows one page, so reload what the new layout expects.
        const pagesListed = this.opportunities.length > this.itemsPerPage;
        if ((isPhone && this.page > 1) || (!isPhone && pagesListed)) this.onRefreshRequest();
        this.phoneSelecting = false;
      },
    },
    opportunities() {
      this.refreshOpportunitySyncs();
    },
    hasInProgressSyncs() {
      this.handleInProgressSyncs();
    },
    '$route.fullPath'(newPath) {
      if (!newPath.startsWith('/opportunities/')) {
        localStorage.setItem('lastOpportunitiesUrl', newPath);
      }
    },
  },
  async created() {
    const hasDocuments = str2bool(this.$route.query.hasDocuments);
    const opportunityStatusIds = getRouteQueryAsArray('opportunityStatusId');
    const voterIds = getRouteQueryAsArray('voterId');
    const authorIds = getRouteQueryAsArray('authorId');
    const assigneeIds = getRouteQueryAsArray('assigneeId');
    const labelIds = getRouteQueryAsArray('labelId');
    const page = getRouteQueryAsNumber('page');
    this.currentPage = page !== undefined ? page : this.currentPage;

    this.setHasDocumentsSearchFilter(hasDocuments);
    this.setOpportunityStatusSearchFilter(opportunityStatusIds);
    this.setVoterSearchFilter(voterIds);
    this.setCreatorSearchFilter(authorIds.length ? authorIds[0] : null);
    this.setAssigneeSearchFilter(assigneeIds);
    this.setLabelSearchFilter(labelIds);
    this.onRefreshRequest(this.currentPage);
    this.handleInProgressSyncs();
    this.getOpportunityStatuses();
    this.getOpportunityLabels();

    this.$track.opportunitiesView();

    bus.$on('last-viewed-opportunity-id', (id) => {
      this.lastViewedOpportunityId = id;
    });
  },
  activated() {
    // This page is kept alive, so nothing re-runs `created` when the user comes back from a details page.
    // Statuses may have changed there (or been refreshed for that single opportunity only), so refresh this
    // page's rows and resume the in-progress polling paused in `deactivated`.
    this._isActive = true;
    this.refreshOpportunitySyncs();
    this.handleInProgressSyncs();
    if (this.lastViewedOpportunityId) {
      this.$nextTick(() => {
        this.highlightOpportunity(this.lastViewedOpportunityId);
        this.lastViewedOpportunityId = null;
      });
    }
  },
  deactivated() {
    this._isActive = false;
    clearInterval(this.syncInterval);
  },
  beforeDestroy() {
    clearInterval(this.syncInterval);
    if (this._syncsCancelSource) this._syncsCancelSource.cancel('Opportunities page destroyed');
  },
};
</script>

<style lang="scss">
.page-opportunities {
  &__header {
    background: $color-white;
    box-shadow: $shadow-dropdown;
    display: flex;
    align-items: center;
    flex-direction: row;
    height: $header-height;
    position: sticky;
    top: $menu-top-bar-height;
    z-index: $header-index;
    padding: 0 32px;
    width: 100%;
    justify-content: space-between;

    @include phone-page-header {
      gap: 12px;
      padding: 0 $phone-page-gutter;
    }

    &-title {
      color: $color-normal;
      display: flex;
      font-family: $font-family-raleway;
      font-size: $font-size-page-title;
      font-weight: normal;
      padding-right: 24px;
      margin: 0 8px 0 0;
      position: relative;
      text-transform: uppercase;

      @include phone-page-title;
    }
  }

  // Phones: just its "+" (phone-main-action).
  &__new {
    @include phone-main-action;
  }

  &__new-label {
    @include screen-below('tablet') {
      @include visually-hidden;
    }
  }

  &__opp-title {
    color: $color-normal;
    display: flex;
    gap: 12px;
    align-items: center;

    &--content {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    &--meta {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 8px;
    }

    &--name {
      font-weight: bold;
    }
  }

  &__external-source {
    background: $color-neutral-pale;
    color: $color-normal-pale;
    border-radius: $border-radius-medium;
    padding: 4px 8px;
    width: max-content;
  }

  &__data-table {
    display: flex;
    flex: 1 auto;
    flex-direction: column;
    padding: 32px;

    @include screen-below('tablet') {
      padding-right: $phone-page-gutter;
      padding-left: $phone-page-gutter;
    }
  }

  // Full-bleed white surface under the sticky header, holding the search + filter chips
  // (mirrors the tenders inbox views panel).
  &__filters-panel {
    background: $color-white;
    box-shadow: $shadow-dropdown;
    margin: -32px -32px 16px;
    padding: 12px 32px 14px;
    position: relative;
    z-index: 2;

    @include phone-filter-panel;
  }

  // Search bar inside the panel — fill/placeholder now come from BaseSearchBarNew's defaults;
  // only the search-specific density (icon offset, padding, font-size) is tweaked here.
  &__search.base-search-bar-container {
    margin-top: 10px;
    margin-bottom: 0;

    // Phones: beside the filters button (PhoneFilterBar).
    @include screen-below('tablet') {
      margin-top: 0;
    }

    .base-search-bar-container__icon {
      color: $color-slate-400;
      left: 13px;

      &.is-focused {
        color: $color-accent;
      }
    }

    .base-search-bar-container__input {
      padding-top: 9px;
      padding-bottom: 9px;
      padding-left: 38px;
      font-size: 13px;
    }
  }

  &__created-at {
    font-size: 12px;
  }

  &__status {
    width: fit-content;

    .base-menu__activator,
    .v-menu {
      display: inline-flex;
      width: fit-content;
    }
  }

  &__labels {
    width: 100%;
    max-width: 100%;

    .base-menu__activator,
    .v-menu {
      display: flex;
      width: 100%;
    }

    .base-labels-menu__badges {
      justify-content: flex-start;
      width: 100%;
    }
  }

  &__vote-action {
    @include inline-add-button;
    gap: 6px;

    .base-icon {
      width: 14px;
      height: 14px;
      flex-shrink: 0;
    }
  }

  // The empty-state buttons stay out of the way until the row is hovered or
  // takes focus, so these columns do not repeat themselves on every row.
  .base-labels-menu__add,
  .base-assignation__add,
  &__vote-action {
    opacity: 0;
    transition: opacity 150ms ease, background-color 150ms ease, color 150ms ease;
  }

  tr:hover,
  tr:focus-within {
    .base-labels-menu__add,
    .base-assignation__add,
    .page-opportunities__vote-action {
      opacity: 1;
    }
  }

  @include no-hover {
    .base-labels-menu__add,
    .base-assignation__add,
    &__vote-action {
      opacity: 1;
    }
  }

  &__content {
    padding-bottom: 96px;
  }

  &__source {
    gap: 4px;
    display: flex;
    align-items: center;
    border-radius: $border-radius-medium;
    background: $color-neutral-pale;
    padding: 4px 8px;
    transition: all 200ms ease;
    color: $color-normal-pale;

    &:hover {
      background-color: $color-hover;
      transition: all 200ms ease;
    }

    &--icon {
      width: 14px;
      height: 14px;
    }

    &--name {
      width: 150px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &--tooltip {
      mark {
        background-color: unset;
        color: unset;
      }
    }

    mark {
      background-color: unset;
      color: unset;
    }
  }

  &__creator {
    display: flex;
    flex-direction: column;
  }

  &__sync {
    display: flex;
    width: min-content;
  }

  &__opp-closing-date {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__vote-section {
    display: flex;
    gap: 16px;
    align-items: center;
  }

  &__final-decision {
    display: flex;
    width: min-content;
    cursor: pointer;

    .base-tag {
      text-transform: uppercase;
      font-weight: $font-weight-bold;
    }
  }

  &__vote-tooltip {
    padding: 8px;
    background-color: $color-normal !important;
    color: $color-white !important;

    .popover-inner,
    .tooltip-inner {
      padding: 0;
    }

    .popover-arrow {
      background-color: $color-normal !important;
    }
  }

  &__vote-tooltip-item {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    align-items: center;
  }

  &__vote-tooltip-content {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__votes {
    display: flex;
    align-items: center;
    gap: 2px;
  }

  &__vote-description {
    display: flex;
    gap: 4px;
  }

  &__pending-vote {
    color: $color-neutral;
  }

  &__results-section {
    display: flex;
    align-items: center;
    // The same room before the list as on the other lists.
    padding-bottom: $space-3;
    justify-content: space-between;

    // The phone's sort and select buttons: the count keeps one line, the buttons go under it.
    @include screen-below('tablet') {
      flex-wrap: wrap;
      row-gap: 4px;

      .page-opportunities__count {
        white-space: nowrap;
      }
    }
  }

  // Beside the refresh button, at the right.
  &__phone-controls {
    margin: 0 8px 0 auto;
  }

  &__count {
    color: $color-neutral;
    font-size: $font-size-small;
    padding: 4px 12px;
    text-transform: uppercase;
  }

  &__button-tooltip {
    margin-bottom: 4px;
  }
}
</style>
